import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import api from './api';

export interface CompanyUser {
  id: string;
  companyId: string;
  email: string;
  displayName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  companyName?: string;
  companyTenantId?: string | null;
}

class AuthService {
  private currentCompanyUser: CompanyUser | null = null;

  /**
   * 企業IDからテナント情報を取得してtenantIdを設定
   */
  async setupTenantByCompanyId(
    companyId: string
  ): Promise<{ success: boolean; error?: string; companyName?: string }> {
    try {
      const response = await api.get(`/company-users/tenant-info/company/${companyId}`);
      if (response.data.success) {
        const { companyTenantId, companyName } = response.data.data;

        // Firebase AuthにtenantIdを設定
        if (companyTenantId) {
          auth.tenantId = companyTenantId;
        }

        return { success: true, companyName };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error: any) {
      console.error('テナント情報の取得に失敗:', error);
      return { success: false, error: 'テナント情報の取得に失敗しました' };
    }
  }

  /**
   * Firebase認証でログイン
   * 注意: companyUser は checkCompanyAccess で取得するため、ここでは取得しません
   */
  async signIn(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Firebase認証成功
      // companyUser は後で checkCompanyAccess で取得される
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * ログアウト
   */
  async signOutUser(): Promise<void> {
    await signOut(auth);
    this.currentCompanyUser = null;
    // tenantIdをクリア
    auth.tenantId = null;
  }

  /**
   * 現在のユーザーが指定した企業にアクセス権限があるかチェック
   */
  async checkCompanyAccess(companyId: string): Promise<{ hasAccess: boolean; user?: CompanyUser }> {
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      return { hasAccess: false };
    }

    // キャッシュされた企業ユーザー情報があるかチェック
    if (this.currentCompanyUser && this.currentCompanyUser.companyId === companyId) {
      return {
        hasAccess: this.currentCompanyUser.isActive,
        user: this.currentCompanyUser,
      };
    }

    // バックエンドから企業ユーザー情報を取得
    const companyUser = await this.fetchCompanyUser(firebaseUser.uid, companyId);

    if (companyUser && companyUser.companyId === companyId && companyUser.isActive) {
      this.currentCompanyUser = companyUser;
      return { hasAccess: true, user: companyUser };
    }

    return { hasAccess: false };
  }

  /**
   * バックエンドから企業ユーザー情報を取得
   * @param firebaseUid Firebase UID
   * @param companyId 企業ID（RLS用）
   */
  private async fetchCompanyUser(firebaseUid: string, companyId: string): Promise<CompanyUser | null> {
    try {
      const response = await api.get(`/company-users/profile/${firebaseUid}?companyId=${encodeURIComponent(companyId)}`);
      if (response.data.success) {
        const companyUser = response.data.data;

        // Firebase AuthにtenantIdを設定
        if (companyUser.companyTenantId) {
          auth.tenantId = companyUser.companyTenantId;
        }

        return companyUser;
      }
      return null;
    } catch (error) {
      console.error('企業ユーザー情報の取得に失敗:', error);
      return null;
    }
  }

  /**
   * 指定したメールアドレスにパスワードリセットメールを送信する
   *
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('パスワードリセットメールの送信に失敗しました:', error);
      throw error;
    }
  }

  /**
   * 現在の企業ユーザー情報を取得
   */
  getCurrentCompanyUser(): CompanyUser | null {
    return this.currentCompanyUser;
  }

  /**
   * Firebase AuthのtenantIdを設定
   */
  setTenantId(tenantId: string | null): void {
    auth.tenantId = tenantId;
  }

  /**
   * 現在設定されているtenantIdを取得
   */
  getCurrentTenantId(): string | null {
    return auth.tenantId;
  }

  /**
   * 認証状態の監視
   * 注意: companyUser は checkCompanyAccess で取得するため、ここでは null を返します
   */
  onAuthStateChanged(callback: (user: User | null, companyUser: CompanyUser | null) => void) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // companyId がわからないため、companyUser はここでは取得しない
        // 後で checkCompanyAccess で取得される
        callback(firebaseUser, this.currentCompanyUser);
      } else {
        this.currentCompanyUser = null;
        callback(null, null);
      }
    });
  }
}

export const authService = new AuthService();
export default authService;
